import { HALF_WIDTH } from '@/dashboards-v2/constants/widget-dimensions.constants.js';
import { defaultSelectedAccount } from '@/vuex/modules/global-filters/default-filters.utils';
import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';
import find from 'lodash/find';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import { i18n_t as $t } from '@utils/i18n';
import { getCatalogCategories } from '@/dashboards-v2/constants/catalog-categories.constants';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

export const NPS_WIDGET_TYPE = Object.freeze({
    DONUT: 'Distribution',
    LINE_GRAPH: 'Trends Over Time',
    RESPONSE: 'Responses',
    SCATTERPLOT: 'Usage By Score',
    METADATATABLE: 'NPS Breakdown',
    PROMOTERS: 'Promoters NPS Themes',
    DETRACTORS: 'Detractors NPS Themes',
    PIE: 'Theme Distribution',
});

const { RESPONSE, LINE_GRAPH, SCATTERPLOT, DONUT, PIE, METADATATABLE, PROMOTERS, DETRACTORS } = NPS_WIDGET_TYPE;

export const NPS_PREMIUM_WIDGET_TYPES = Object.freeze([SCATTERPLOT, METADATATABLE]);

export const NPS_LINE_GRAPH_TIMEFRAMES = Object.freeze({
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
});

export const DEFAULT_PAGE_FEATURE_OPTION = { name: 'Total' };

const DEFAULT_DATE_RANGE = {
    type: 'Last 30 Days',
    timeSeries: {
        config: {
            type: 'Last 30 Days',
        },
        spec: {
            count: 30,
        },
    },
};

/**
 * Init undefined props to null because keys of undefined props do not get stored +
 * https://stackoverflow.com/questions/42678983/vue-js-computed-property-not-updating
 */
const getDefaultNpsOptionsBase = ({
    account = defaultSelectedAccount,
    appId = null,
    channel = 'inAppPlusEmail',
    createdAt = null,
    dateRange = DEFAULT_DATE_RANGE,
    guideId = null,
    guideStepId = null,
    languageCode = '*',
    metadata = null,
    npsScatterPlotFilter = DEFAULT_PAGE_FEATURE_OPTION,
    pollFollowUp = null,
    pollId = null,
    question = null,
    segmentId = 'everyone',
    sinceGuideCreation = false,
    timeframe = NPS_LINE_GRAPH_TIMEFRAMES.WEEKLY,
    widgetType = DONUT,
} = {}) => ({
    appId,
    account,
    channel,
    createdAt,
    dateRange,
    guideId,
    guideStepId,
    languageCode,
    metadata,
    npsScatterPlotFilter,
    pollFollowUp,
    pollId,
    question,
    segmentId,
    sinceGuideCreation,
    timeframe,
    widgetType,
});

const getNpsWidgetBase = (guideName) => ({
    name: guideName
        ? $t('npsWidgetOptions.Net Promoter Score {guideName}', { guideName })
        : $t('npsWidgetOptions.Net Promoter Score'),
    type: 'npsWidget',
    width: HALF_WIDTH,
    description: $t(
        'npsWidgetOptions.Understand user sentiment for your app through NPS distribution, trends, verbatim responses, promoter and detractor themes, and usage by score.',
    ),
    productLines: [PRODUCT_LINES.NPS],
});

export const getDefaultNpsWidget = ({ guideName, ...rest } = {}) => ({
    ...getNpsWidgetBase(guideName),
    definition: getDefaultNpsOptionsBase(rest),
});

export const getNpsWidgetCatalogSettings = () =>
    Object.freeze({
        ...getNpsWidgetBase(),
        settings: getDefaultNpsOptionsBase(),
        image: 'nps-widget.png',
        categories: [getCatalogCategories().GUIDES],
    });

export function getWidgetSettings (widgetType) {
    const base = ['name', 'widget-type', 'guide', 'channel', 'accountChooser'];

    switch (widgetType) {
        case SCATTERPLOT:
            return [...base, 'timeSeriesPicker', 'page-feature-setting', 'segmentChooser'];
        case LINE_GRAPH:
            return [...base, 'timeSeriesPicker', 'view-timeframe-by', 'segmentChooser'];
        case DONUT:
            return [...base, 'all-time', 'timeSeriesPicker', 'segmentChooser'];
        case METADATATABLE:
        case PROMOTERS:
        case DETRACTORS:
        case RESPONSE:
        case PIE:
        default:
            return [...base, 'all-time', 'timeSeriesPicker', 'segmentChooser'];
    }
}

export const getNPSPollFromGuide = (guide) =>
    guide.polls?.find((x) => x.attributes && x.attributes.type && x.attributes.type === 'NPSRating');

export const getGuideSettings = (guide) => {
    if (guide == null) return { guideId: null };
    const { appId, createdAt, name, steps } = guide;
    const poll = getNPSPollFromGuide(guide) || {};
    const step = find(steps, (step) => {
        return includes(step.pollIds, poll.id);
    });

    return {
        guideId: guide.id || null,
        guideStepId: step && step.id,
        createdAt,
        appId,
        guideName: name,
    };
};

export const getPollSettingsFromPoll = (poll) => {
    if (poll == null) return { pollId: null, pollFollowUp: undefined };

    return {
        pollId: poll.id || null,
        pollFollowUp: poll.attributes ? poll.attributes.followUp : undefined,
        question: poll.question,
    };
};

/** Where guideValue === full guide populated through Vuex to ensure still exists */
export function getNpsWidgetConfigError (configOptions, guideValue) {
    const { widgetType, npsScatterPlotFilter, guideId, metadata } = configOptions;

    return {
        noGuide: !guideId,
        guideInvalid: !!guideId && !guideValue,
        npsScatterPlotFilter: widgetType === SCATTERPLOT && !npsScatterPlotFilter,
        metadataFieldInvalid: widgetType === METADATATABLE && isEmpty(metadata),
    };
}

export function getNpsTimeframeOptions (dayCount) {
    const timeframes = Object.values(NPS_LINE_GRAPH_TIMEFRAMES);

    return dayCount >= 90 ? timeframes.filter((item) => item !== 'daily') : timeframes;
}

export const getPollWidgetAsNpsWidget = ({ pollWidget, guide } = {}) => {
    const poll = getNPSPollFromGuide(guide);
    const { createdAt, appId, guideStepId } = getGuideSettings(guide);

    const npsWidgetConfig = getDefaultNpsWidget({
        ...pollWidget.definition,
        ...getPollSettingsFromPoll(poll),
        guideStepId,
        createdAt,
        appId,
    });

    npsWidgetConfig.id = pollWidget.id;
    npsWidgetConfig.name = pollWidget.name;
    npsWidgetConfig.definition.layout = pollWidget.definition.layout;

    // Update legacy date ranges
    if (!npsWidgetConfig.definition.dateRange.timeSeries) {
        try {
            npsWidgetConfig.definition.dateRange.timeSeries = convertConfigToTimeSeries(
                npsWidgetConfig.definition.dateRange,
            );
        } catch {
            npsWidgetConfig.definition.dateRange = DEFAULT_DATE_RANGE;
        }
    }

    return npsWidgetConfig;
};
